import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Card } from "antd";
import { fetchClient } from "../../api/clientsApi";
import {
  selectCurrentClient,
  selectClientsLoading
} from "../../store/slices/clients.slice";

// AntD Components
import { Skeleton } from "antd";

// Sub-components
const ClientInfoSection = ({ clientDetails }) => {
  if (!clientDetails) {
    return <Skeleton active title paragraph={{ rows: 2 }} />;
  }

  const { name, email, phone, dob } = clientDetails;
  return (
    <Card title="Client Info" style={{ marginBottom: 16 }}>
      <p><b>Name:</b> {name || "N/A"}</p>
      <p><b>Email:</b> {email || "N/A"}</p>
      <p><b>Phone:</b> {phone || "N/A"}</p>
      <p><b>Date of Birth:</b> {dob ? dob.slice(0, 10) : "N/A"}</p>
    </Card>
  );
};

const PreferencesSection = ({ preferences }) => {
  if (!preferences) {
    return <Skeleton active title paragraph={{ rows: 3 }} />;
  }

  const {
    risk_score,
    target_equity,
    target_fixed,
    min_cash,
    distribution_preferences
  } = preferences;

  return (
    <Card title="Preferences" style={{ marginBottom: 16 }}>
      <p><b>Risk Score:</b> {risk_score ?? "N/A"}</p>
      <p><b>Target Equity:</b> {target_equity ?? "N/A"}%</p>
      <p><b>Target Fixed:</b> {target_fixed ?? "N/A"}%</p>
      <p><b>Min Cash:</b> {min_cash ?? "N/A"}</p>
      <p>
        <b>Distribution Preferences:</b>{" "}
        {JSON.stringify(distribution_preferences) !== "{}"
          ? JSON.stringify(distribution_preferences)
          : "None"}
      </p>
    </Card>
  );
};

const CustodiansSection = ({ custodians }) => {
  if (!custodians) {
    return <Skeleton active title paragraph={{ rows: 1 }} />;
  }

  // If no custodians array or length=0, show a placeholder
  if (custodians.length === 0) {
    return (
      <Card title="Custodians" style={{ marginBottom: 16 }}>
        <p>No custodians found.</p>
      </Card>
    );
  }

  return (
    <Card title="Custodians" style={{ marginBottom: 16 }}>
      {custodians.map((c, idx) => (
        <p key={idx}>
          <b>Name:</b> {c.custodian_name || "N/A"} |
          <b> Account#:</b> {c.account_number || "N/A"}
        </p>
      ))}
    </Card>
  );
};

const AddressesSection = ({ addresses }) => {
  if (!addresses) {
    return <Skeleton active title paragraph={{ rows: 1 }} />;
  }

  if (addresses.length === 0) {
    return (
      <Card title="Addresses" style={{ marginBottom: 16 }}>
        <p>No addresses found.</p>
      </Card>
    );
  }

  return (
    <Card title="Addresses" style={{ marginBottom: 16 }}>
      {addresses.map((addr, idx) => (
        <div key={idx} style={{ marginBottom: 8 }}>
          <p><b>Type:</b> {addr.type}</p>
          <p>
            <b>Address:</b> {addr.line1}, {addr.city}
          </p>
        </div>
      ))}
    </Card>
  );
};

/**
 * Main Dashboard for a single client
 */
const ClientDashboard = () => {
  const { clientId } = useParams();
  const dispatch = useDispatch();

  const currentClient = useSelector(selectCurrentClient);
  const isLoading = useSelector(selectClientsLoading);

  useEffect(() => {
    if (clientId) {
      dispatch(fetchClient(clientId));
    }
  }, [clientId, dispatch]);

  if (isLoading) return <p>Loading client...</p>;
  if (!currentClient) return <p>No client data available.</p>;

  const {
    client_details,
    preferences,
    custodians,
    addresses
    // Add more fields if needed: securities, tamps, etc.
  } = currentClient;

  return (
    <div style={{ margin: 16 }}>
      <ClientInfoSection clientDetails={client_details} />
      <PreferencesSection preferences={preferences} />
      <CustodiansSection custodians={custodians} />
      <AddressesSection addresses={addresses} />
      {/* Additional sections for securities, tamps, etc. */}
    </div>
  );
};

export default ClientDashboard;
